import { RefObject, useCallback, useRef } from 'react';

type UseDraggableScrollProps = {
  ref: RefObject<HTMLElement>;
  direction?: 'vertical' | 'horizontal' | 'both';
};

type UseDraggableScrollState = {
  scrollTop: number;
  scrollLeft: number;
  mouseX: number;
  mouseY: number;
};

type UseDraggableScrollReturn = {
  onMouseDown: (event: React.MouseEvent) => void;
  updateScroll: (x?: number, y?: number) => void;
};

export function useDraggableScroll({
  ref,
  direction = 'both',
}: UseDraggableScrollProps): UseDraggableScrollReturn {
  const scrollStatus = useRef<UseDraggableScrollState>({
    scrollTop: 0,
    scrollLeft: 0,
    mouseX: 0,
    mouseY: 0,
  });

  const mouseMoveHandler = useCallback(
    (event: { clientX: number; clientY: number }) => {
      if (ref?.current) {
        const { mouseX, mouseY, scrollLeft, scrollTop } = scrollStatus.current;

        const dx = event.clientX - mouseX;
        const dy = event.clientY - mouseY;

        updateScroll(scrollLeft - dx, scrollTop - dy);
      }
    },
    [ref],
  );

  const mouseUpHandler = useCallback(() => {
    if (ref?.current) {
      ref.current.style.cursor = 'grab';

      document.removeEventListener('mousemove', mouseMoveHandler);
      document.removeEventListener('mouseup', mouseUpHandler);
    }
  }, [mouseMoveHandler, ref]);

  const onMouseDown = useCallback(
    (event: { clientX: number; clientY: number }) => {
      if (ref?.current) {
        scrollStatus.current = {
          scrollLeft: ref.current.scrollLeft,
          scrollTop: ref.current.scrollTop,
          mouseX: event.clientX,
          mouseY: event.clientY,
        };

        ref.current.style.cursor = 'grabbing';
        ref.current.style.userSelect = 'none';

        document.addEventListener('mousemove', mouseMoveHandler);
        document.addEventListener('mouseup', mouseUpHandler);
      }
    },
    [mouseMoveHandler, mouseUpHandler, ref],
  );

  const updateScroll = useCallback(
    (x = 0, y = 0) => {
      if (ref?.current) {
        const yScroll = y > 0 ? y : 0;
        if (direction !== 'horizontal' && ref?.current?.scrollTop !== yScroll) {
          ref.current.scrollTop = yScroll;
        }

        const xScroll = x > 0 ? x : 0;
        if (direction !== 'vertical' && ref?.current?.scrollLeft !== xScroll) {
          ref.current.scrollLeft = xScroll;
        }
      }
    },
    [ref],
  );

  return { onMouseDown, updateScroll };
}
